<template>
  <AppDrawer
    :model-value="isSidebarOpen"
    side="right"
    size="xl"
    :show-close="false"
    body-class="px-5 py-5"
    @update:model-value="closeChangelog"
  >
    <template #header>
      <div class="min-w-0 flex-1 space-y-4">
        <div class="flex items-start justify-between gap-2">
          <PageTabs
            v-model="surface"
            :tabs="surfaceTabs"
            :aria-label="t('changelog.sidebar.products')"
            class="!justify-start"
          />
          <AppIconButton
            :label="t('ui.tool_help.close')"
            variant="ghost"
            tone="neutral"
            size="sm"
            @click="closeChangelog"
          >
            <Icon :path="mdiClose" size="18" />
          </AppIconButton>
        </div>
        <div class="flex items-center justify-between gap-4">
          <DialogTitle as="h2" class="text-lg font-semibold leading-6 text-theme-text">
            {{ t('changelog.sidebar.title') }}
          </DialogTitle>
          <AppSelect
            v-model="selectedVersion"
            :options="versionOptions"
            :aria-label="t('changelog.sidebar.version')"
            :disabled="isLoading || !versionOptions.length"
            size="sm"
            container-class="w-36 shrink-0"
          />
        </div>
      </div>
    </template>

    <p v-if="isLoading" role="status" class="text-sm text-theme-text-secondary">
      {{ t('general.loading') }}
    </p>
    <div v-else-if="loadError" role="alert" class="space-y-3">
      <p class="text-sm text-theme-text-secondary">{{ t('changelog.sidebar.error') }}</p>
      <AppButton size="sm" variant="outline" @click="refresh">
        {{ t('dataset.error.try_again') }}
      </AppButton>
    </div>
    <section v-else :aria-label="`${t(`changelog.surface.${surface}`)} ${selectedVersion}`">
      <p v-if="releaseDate" class="mb-5 text-xs text-theme-text-muted">{{ releaseDate }}</p>
      <ChangelogEntryList v-if="entries.length" :entries="entries" />
      <p v-else class="text-sm text-theme-text-secondary">{{ t('changelog.sidebar.empty') }}</p>
    </section>
  </AppDrawer>
</template>

<script setup>
  import Icon from '@/components/Icon';
  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import AppIconButton from '@/components/ui/buttons/AppIconButton.vue';
  import AppSelect from '@/components/ui/forms/AppSelect.vue';
  import PageTabs from '@/components/ui/nav/PageTabs.vue';
  import AppDrawer from '@/components/ui/overlays/AppDrawer.vue';
  import ChangelogEntryList from '@/features/changelog/components/ChangelogEntryList.vue';
  import { useChangelog } from '@/features/changelog/composables/useChangelog';
  import { formatDate } from '@/i18n/formatters';
  import { DialogTitle } from '@headlessui/vue';
  import { mdiClose } from '@mdi/js';
  import { computed, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  const SURFACES = ['portal', 'mcp', 'api', 'office'];
  const { t, locale } = useI18n();
  const {
    releases, versions, isSidebarOpen, isLoading, loadError,
    loadChangelog, markChangelogSeen, closeChangelog,
  } = useChangelog();
  const surface = ref('portal');
  const chosenVersion = ref('');
  const fresh = ref(false);

  const surfaceTabs = computed(() => SURFACES.map((value) => ({
    value,
    label: `${t(`changelog.surface.${value}`)} ${versions.value[value] || '—'}`,
  })));
  const surfaceReleases = computed(() => releases.value.filter((release) =>
    release.entries?.some((entry) => entry.surface === surface.value && entry.audience !== 'internal')
  ));
  const versionOptions = computed(() => {
    const latest = versions.value[surface.value];
    const available = [latest, ...surfaceReleases.value.map((release) => release.versions?.[surface.value])];
    return [...new Set(available.filter(Boolean))].map((value) => ({
      value,
      label: value === latest ? `${value} · ${t('changelog.sidebar.latest')}` : value,
    }));
  });
  const selectedVersion = computed({
    get: () => chosenVersion.value || versionOptions.value[0]?.value || '',
    set: (value) => { chosenVersion.value = value; },
  });
  const selectedReleases = computed(() => surfaceReleases.value.filter((release) =>
    release.versions?.[surface.value] === selectedVersion.value
  ));
  const entries = computed(() => selectedReleases.value.flatMap((release) =>
    release.entries.filter((entry) => entry.surface === surface.value && entry.audience !== 'internal')
  ));
  const releaseDate = computed(() => {
    const date = selectedReleases.value[0]?.released_at;
    return date ? formatDate(date, locale.value, { dateStyle: 'long' }) : '';
  });

  async function refresh() {
    fresh.value = false;
    await loadChangelog({ limit: 100 });
    fresh.value = !loadError.value;
  }

  watch(isSidebarOpen, (open) => {
    if (!open) return;
    surface.value = 'portal';
    chosenVersion.value = '';
    refresh();
  }, { immediate: true });
  watch(surface, () => { chosenVersion.value = ''; });
  watch([isSidebarOpen, surface, selectedVersion, fresh], () => {
    if (isSidebarOpen.value && fresh.value && entries.value.length &&
        selectedVersion.value === versions.value[surface.value]) {
      markChangelogSeen({ [surface.value]: selectedVersion.value });
    }
  });
</script>
